import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth } from 'react-oidc-context'
import { setAccessToken } from './auth/tokenStore'

// Shows the user's name + sign-out when authenticated, or a sign-in button otherwise.
function AuthButton() {
  const auth = useAuth()

  if (auth.isLoading) {
    return <span className="text-gray-400 text-sm">…</span>
  }

  if (auth.isAuthenticated) {
    const name = auth.user?.profile.given_name || auth.user?.profile.name || auth.user?.profile.email
    return (
      <div className="flex items-center space-x-3">
        <span className="hidden sm:inline text-gray-600 text-sm">Hi, {name}</span>
        <button
          onClick={() => auth.signoutRedirect()}
          className="text-gray-600 hover:text-amber-600 font-medium transition-colors"
        >
          Sign out
        </button>
      </div>
    )
  }

  return (
    <button
      onClick={() => auth.signinRedirect()}
      className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-5 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-amber-500/25 transition-all"
    >
      Sign in
    </button>
  )
}

// Rendered at /callback while the OIDC redirect is being processed.
function AuthCallback() {
  const auth = useAuth()
  const navigate = useNavigate()

  // Once react-oidc-context has finished the code->token exchange, hand control back
  // to react-router so the SPA leaves /callback and the query string is dropped.
  useEffect(() => {
    if (!auth.isLoading && (auth.isAuthenticated || auth.error)) {
      navigate('/', { replace: true })
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-500 border-t-transparent mx-auto mb-4" />
        <p className="text-gray-600">{auth.error ? `Sign-in error: ${auth.error.message}` : 'Signing you in…'}</p>
      </div>
    </div>
  )
}

function MainLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
        <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <Link to="/" className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                  </div>
                  <span className="text-xl font-bold bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent">TreasureLog</span>
                </Link>
              </div>
              <div className="hidden md:flex items-center space-x-8">
                <Link to="/" className="text-gray-600 hover:text-amber-600 font-medium transition-colors">Home</Link>
                <Link to="/collections" className="text-gray-600 hover:text-amber-600 font-medium transition-colors">Collections</Link>
                <Link to="/wishlist" className="text-gray-600 hover:text-amber-600 font-medium transition-colors">Wishlist</Link>
                <Link to="/about" className="text-gray-600 hover:text-amber-600 font-medium transition-colors">About</Link>
                <button className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-5 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-amber-500/25 transition-all">
                  Add Item
                </button>
                <AuthButton />
              </div>
              <div className="md:hidden flex items-center">
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-600 p-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t">
              <div className="px-4 py-3 space-y-3">
                <Link to="/" className="block text-gray-600 hover:text-amber-600 font-medium">Home</Link>
                <Link to="/collections" className="block text-gray-600 hover:text-amber-600 font-medium">Collections</Link>
                <Link to="/wishlist" className="block text-gray-600 hover:text-amber-600 font-medium">Wishlist</Link>
                <Link to="/about" className="block text-gray-600 hover:text-amber-600 font-medium">About</Link>
              </div>
            </div>
          )}
        </nav>

        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/collections" element={<Collections />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/about" element={<About />} />
          </Routes>
        </main>

        <footer className="bg-gray-900 text-gray-400 py-8 mt-20">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p>&copy; 2025 TreasureLog. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  )
}

function App() {
  const [triedSilent, setTriedSilent] = useState(false)
  const auth = useAuth()

  // Keep the axios token store in sync with the current session so API calls
  // carry the bearer token. Runs whenever the auth user changes.
  useEffect(() => {
    setAccessToken(auth.user?.access_token ?? null)
  }, [auth.user])

  // Zero-click SSO: on first load, silently ask the ID server (prompt=none in a hidden
  // iframe) whether there's an existing session. If yes, we sign in with no interaction;
  // if not, the call fails quietly and the user stays logged out (login is optional).
  // Only same-site origins (e.g. *.local.letech.solutions) can pass the cookie here.
  useEffect(() => {
    if (auth.isLoading || auth.isAuthenticated || triedSilent) return
    const path = window.location.pathname
    if (path.startsWith('/callback') || path.startsWith('/silent-renew')) return
    setTriedSilent(true)
    auth.signinSilent().catch(() => { /* no existing session — stay anonymous */ })
  }, [auth, auth.isLoading, auth.isAuthenticated, triedSilent])

  return (
    <Router>
      <Routes>
        {/* OIDC sign-in landing. react-oidc-context processes the code/state on load
            and the AuthCallback component sends the user back to "/". */}
        <Route path="/callback" element={<AuthCallback />} />
        {/* Hidden iframe target for silent token renewal. */}
        <Route path="/silent-renew" element={<div />} />
        {/* All other pages use the main layout. */}
        <Route path="*" element={<MainLayout />} />
      </Routes>
    </Router>
  )
}

function Home() {
  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      title: 'Track Collections',
      description: 'Log every item in your kids\' collections - Akedo, Pokémon, LOL Dolls, and more.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
      ),
      title: 'Share with Family',
      description: 'Generate shareable links so grandparents and family know what to buy.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      title: 'Wishlist Mode',
      description: 'Mark items as "owned" or "wanted" so gift-givers never buy duplicates.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Photo Catalog',
      description: 'Snap photos of each item to create a visual catalog of the collection.'
    }
  ]

  const collections = [
    { name: 'Akedo', count: 47, color: 'from-red-500 to-orange-500' },
    { name: 'Pokémon Cards', count: 234, color: 'from-yellow-500 to-amber-500' },
    { name: 'LOL Dolls', count: 28, color: 'from-pink-500 to-rose-500' },
    { name: 'Hot Wheels', count: 156, color: 'from-blue-500 to-cyan-500' },
  ]

  return (
    <>
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 py-20 sm:py-32">
        <div className="text-center">
          <div className="inline-flex items-center bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            No more duplicate gifts!
          </div>
          <h1 className="text-5xl sm:text-7xl font-bold text-gray-900 mb-6">
            Catalog Your Kids'
            <span className="block bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent">
              Treasured Collections
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10">
            Track what they have, share wishlists with family, and never get duplicate
            gifts again. Perfect for Akedo, Pokémon, LOL Dolls, and more.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl hover:shadow-amber-500/25 transition-all transform hover:-translate-y-1">
              Start a Collection
            </button>
            <button className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold text-lg hover:border-amber-500 hover:text-amber-600 transition-all">
              See How It Works
            </button>
          </div>
        </div>
      </section>

      {/* Sample Collections */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Popular Collection Types</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {collections.map((collection, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg shadow-gray-200/50 hover:shadow-xl transition-all cursor-pointer">
              <div className={`w-12 h-12 bg-gradient-to-br ${collection.color} rounded-xl flex items-center justify-center text-white text-xl font-bold mb-4`}>
                {collection.name.charAt(0)}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{collection.name}</h3>
              <p className="text-gray-600 text-sm">{collection.count} items tracked</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Everything you need</h2>
          <p className="text-xl text-gray-600">Keep track of every treasure in your kids' collections</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg shadow-gray-200/50 hover:shadow-xl hover:shadow-amber-500/10 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-yellow-100 rounded-xl flex items-center justify-center text-amber-600 mb-4">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How Sharing Works */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-r from-amber-500 to-yellow-500 rounded-3xl p-12 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Share with Family</h2>
            <p className="text-xl opacity-90 mb-8">
              Generate a shareable link that shows what your kids already have and what's on their wishlist.
              Grandparents, aunts, and uncles will always know exactly what to buy.
            </p>
            <div className="bg-white/20 backdrop-blur rounded-xl p-4 inline-block">
              <code className="text-lg">treasurelog.app/share/emma-akedo</code>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="bg-white rounded-3xl p-12 text-center shadow-xl shadow-gray-200/50">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Ready to get organized?</h2>
          <p className="text-xl text-gray-600 mb-8">Start cataloging your kids' collections today.</p>
          <button className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl hover:shadow-amber-500/25 transition-all transform hover:-translate-y-1">
            Create Free Account
          </button>
        </div>
      </section>
    </>
  )
}

function Collections() {
  return (
    <section className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Your Collections</h1>
        <button className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-5 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-amber-500/25 transition-all">
          + New Collection
        </button>
      </div>
      <div className="bg-white rounded-2xl shadow-lg shadow-gray-200/50 p-12 text-center">
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">No collections yet</h2>
        <p className="text-gray-600 mb-6">Start by creating a collection for your kids' toys or cards.</p>
        <button className="text-amber-600 font-medium hover:text-amber-700">Create your first collection →</button>
      </div>
    </section>
  )
}

function Wishlist() {
  return (
    <section className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Wishlist</h1>
        <button className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-5 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-amber-500/25 transition-all">
          Share Wishlist
        </button>
      </div>
      <div className="bg-white rounded-2xl shadow-lg shadow-gray-200/50 p-12 text-center">
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">No wishlist items</h2>
        <p className="text-gray-600 mb-6">Add items from collections to the wishlist and share with family.</p>
        <button className="text-amber-600 font-medium hover:text-amber-700">Browse collections →</button>
      </div>
    </section>
  )
}

function About() {
  return (
    <section className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">About TreasureLog</h1>
      <div className="prose prose-lg text-gray-600">
        <p className="text-xl mb-6">
          TreasureLog helps you catalog your kids' collections and share wishlists
          with family and friends.
        </p>
        <p className="mb-6">
          Does your kid collect Akedo warriors, Pokémon cards, or LOL Dolls? TreasureLog
          makes it easy to track what they already have and create wishlists to share with
          grandparents and relatives - so everyone knows exactly what to buy for birthdays
          and holidays.
        </p>
        <div className="bg-amber-50 rounded-2xl p-6 border border-amber-100">
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Perfect For</h3>
          <ul className="space-y-2 text-gray-700">
            <li>• Action figure collections (Akedo, Marvel, etc.)</li>
            <li>• Trading cards (Pokémon, Yu-Gi-Oh!, etc.)</li>
            <li>• Dolls and playsets (LOL, Barbie, etc.)</li>
            <li>• Die-cast cars (Hot Wheels, Matchbox)</li>
            <li>• Any collectible toys or items</li>
          </ul>
        </div>
      </div>
    </section>
  )
}

export default App
